/**
 * Table AST Nodes
 *
 * Block-level layout nodes for table structure:
 * TableBlock → TableRow[] → TableCell[] → InlineNode[]
 */

import { z } from 'zod';
import { InlineNodeSchema } from './inline';
import { AST_TYPES } from '../constants';

// ═══════════════════════════════════════════════════════════════════════════
// Table Cell
// ═══════════════════════════════════════════════════════════════════════════

export const TableCellSchema = z.object({
  type: z.literal(AST_TYPES.TABLE_CELL),
  colspan: z.number().int().positive().optional(),
  inlines: z.array(InlineNodeSchema)
});

export type TableCell = z.infer<typeof TableCellSchema>;

export function createTableCell(
  inlines: z.infer<typeof InlineNodeSchema>[],
  colspan?: number
): TableCell {
  return TableCellSchema.parse({
    type: AST_TYPES.TABLE_CELL,
    colspan,
    inlines
  });
}

export function isTableCell(value: unknown): value is TableCell {
  return TableCellSchema.safeParse(value).success;
}

// ═══════════════════════════════════════════════════════════════════════════
// Table Row
// ═══════════════════════════════════════════════════════════════════════════

export const TableRowSchema = z.object({
  type: z.literal(AST_TYPES.TABLE_ROW),
  cells: z.array(TableCellSchema).min(1)
});

export type TableRow = z.infer<typeof TableRowSchema>;

export function createTableRow(cells: TableCell[]): TableRow {
  return TableRowSchema.parse({
    type: AST_TYPES.TABLE_ROW,
    cells
  });
}

export function isTableRow(value: unknown): value is TableRow {
  return TableRowSchema.safeParse(value).success;
}

// ═══════════════════════════════════════════════════════════════════════════
// Table Block
// ═══════════════════════════════════════════════════════════════════════════

export const TableBlockSchema = z.object({
  type: z.literal(AST_TYPES.TABLE_BLOCK),
  cols: z.number().int().positive(),
  rows: z.array(TableRowSchema).min(1)
});

export type TableBlock = z.infer<typeof TableBlockSchema>;

export function createTableBlock(
  cols: number,
  rows: TableRow[]
): TableBlock {
  return TableBlockSchema.parse({
    type: AST_TYPES.TABLE_BLOCK,
    cols,
    rows
  });
}

export function isTableBlock(value: unknown): value is TableBlock {
  return TableBlockSchema.safeParse(value).success;
}
